import type React from 'react';
import { useEffect, useState } from 'react';
import { Col, Form, type FormInstance, Input, Row } from 'antd';
import ParListSelect from 'components/ParListSelect';
import FormInput from 'components/FormInput';
import { init } from 'i18next';

export type BankProps = {
  form?: FormInstance;
  ibanField?: string;
  bicField?: string;
  banknameField?: string;
  bkHolder?: string;
  countcode: string;
  currcode: string;
  notRequired?: boolean;
  disabled?: boolean;
  action: string;
  className?: string;
};

const Bank: React.FC<BankProps> = ({
  disabled = false,
  countcode,
  currcode,
  bkHolder = 'bkholder',
  ibanField = 'tpiban',
  bicField = 'tpbic',
  banknameField = 'tpbankname',
  action,
  form,
  className,
}) => {
  const [countryCode, setCountryCode] = useState<string>(countcode || 'FR');
  const [ibanRest, setIbanRest] = useState<string>('');

  useEffect(() => {
    const fullIban = countryCode + ibanRest;
    form?.setFieldsValue({
      // [ibanField]: fullIban,
      tpiban: fullIban,
      bkcode: fullIban.slice(0, 4)
    });
  }, [countryCode, ibanRest, form, ibanField]);

  const ibanValidator = (iban: string): boolean => {
    if (iban.length < 27) return false;
    if (!/^[A-Z]{2}/.test(iban)) return false;
    // if (!/^[A-Z]{2}[A-Z0-9]$/.test(iban)) return false
    return true;
  };

  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value?.toUpperCase().replace(/\s/g, '');
    setIbanRest(inputValue);

    const fullIban = countryCode + inputValue;
    // if (fullIban.length > 27) {
    //   setIbanRest(inputValue)
    // }

    form?.setFieldsValue({
      [ibanField]: fullIban,
      tpiban: fullIban,
      bkcode: fullIban.slice(0, 4),
      bkbranchcode: fullIban.slice(9, 14),
      bkaccountnum: fullIban.slice(14, 25),
      bkaccountkey: fullIban.slice(25, 27),
    });
  };

  return (
    <Row className={className}>
      {action !== 'edit' && (
        <FormInput
          col
          md={24}
          name="countcode"
          rules={[{ required: true }]}
          nolabel
          initialValue={countryCode}
          hidden={action === 'edit'}
          style={{ marginBottom: 10 }}
          noSpecialChars
        >
          <ParListSelect
            parcode="COUNTRY"
            className="ut-input-modal no-border-radius"
            placeholder="Pays*"
            style={{ height: 33, borderRadius: 0 }}
            onChange={(value) => {
              const newCountryCode = value.toUpperCase();
              setCountryCode(newCountryCode);
              const currentIban = form?.getFieldValue(ibanField) || '';
              form?.setFieldsValue({
                [ibanField]: newCountryCode + currentIban.slice(2),
              });
            }}
          />
        </FormInput>
      )}

      <FormInput
        col
        md={24}
        name={banknameField}
        nolabel
        style={{ marginBottom: 10 }}
        noSpecialChars
      >
        <Input
          className="ut-input-modal"
          disabled={disabled}
          placeholder="Libelle du compte*"
          style={{ height: 33, borderRadius: 0 }}
        />
      </FormInput>

      {action !== 'edit' && (
        <FormInput
          col
          md={24}
          name={bkHolder}
          rules={[{ required: true }]}
          hidden={action === 'edit'}
          nolabel
          style={{ marginBottom: 10 }}
          noSpecialChars
        >
          <Input
            disabled={disabled}
            className="ut-input-modal"
            placeholder="Titulaire*"
            style={{ height: 33, borderRadius: 0 }}
          />
        </FormInput>
      )}

      {action !== 'edit' && (
        <FormInput
          col
          md={24}
          name={ibanField}
          hidden={action === 'edit'}
          nolabel
          noSpecialChars
          style={{ marginBottom: 10 }}
          rules={[
            { required: true, message: "L'IBAN est requis" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) return Promise.resolve()
                const fullIban = countryCode + ibanRest
                const checkCode = fullIban.slice(2, 4)
                if (!/^\d{2}$/.test(checkCode)) {
                  return Promise.reject(new Error("Les deux caractères après le code pays doivent être numériques"))
                }
                if (!ibanValidator(fullIban)) {
                  return Promise.reject(new Error("IBAN invalide"))
                }
                return Promise.resolve()
              },
            }),
          ]}
        >

          <div className="d-flex align-center">
            <Input
              disabled
              className="ut-input-modal no-border-radius"
              value={countryCode}
              style={{ height: 33, width: 50 }}
              defaultValue={countcode}
            />
            <Input
              disabled={disabled}
              className="ut-input-modal flex-1"
              placeholder="IBAN*"
              value={ibanRest}
              onChange={handleIBANChange}
              style={{ height: 33, borderRadius: 0 }}
            />
          </div>
        </FormInput>
      )}

      {action !== 'edit' && (
        <FormInput
          col
          md={24}
          name={bicField}
          nolabel
          hidden={action === 'edit'}
          style={{ marginBottom: 10 }}
          rules={[{ required: true }]}
          noSpecialChars
        >
          <Input
            disabled={disabled}
            className="ut-input-modal"
            placeholder="BIC*"
          />
        </FormInput>
      )}

      <Form.Item name="bkcode" hidden />
      <Form.Item name="bkbranchcode" hidden />
      <Form.Item name="bkaccountnum" hidden />
      <Form.Item name="bkaccountkey" hidden />
    </Row>
  );
};

export default Bank;
